import { evaluateDynamicFilters, IAnyObject, IMatchData } from '@shesha-io/reactjs';
import { FilterExpression, IStoredFilter } from '@shesha-io/reactjs/dist/providers/dataTable/interfaces';
import { NestedPropertyMetadatAccessor } from '@shesha-io/reactjs/dist/providers/metadataDispatcher/contexts';

interface IMatchDataWithPreparation extends IMatchData {
  prepare?: (data: any) => any;
}

export interface UseEvaluatedFilterArgs {
  filter?: FilterExpression;
  mappings: IMatchDataWithPreparation[];
  metadataAccessor?: NestedPropertyMetadatAccessor;
}

export const evaluateFilterAsync = async (args: UseEvaluatedFilterArgs): Promise<string> => {
  const { filter, mappings, metadataAccessor } = args;

  if (!filter) return '';

  const preparedMappings: IMatchData[] = [];
  mappings.forEach((item) => {
    const { prepare, ...restItemProps } = item;
    const preparedData = item.prepare ? item.prepare(item.data) : item.data;
    preparedMappings.push({ ...restItemProps, data: preparedData });
  });

  // Evaluate the filters asynchronously
  const response = await evaluateDynamicFilters(
    [{ expression: filter } as IStoredFilter],
    preparedMappings,
    metadataAccessor,
  );

  // Return the evaluated filter expression or an empty string if it's not found
  return JSON.stringify(response[0]?.expression) || '';
};

export const evaluateFilters = async (
  item: any,
  formData: any,
  globalState: IAnyObject,
  propertyMetadataAccessor: NestedPropertyMetadatAccessor,
): Promise<string> => {
  if (!item.filters) return '';

  const evaluatedFilters = await evaluateFilterAsync({
    filter: item.filters,
    mappings: [
      {
        match: 'data',
        data: formData,
      },
      {
        match: 'globalState',
        data: globalState,
      },
    ],
    metadataAccessor: propertyMetadataAccessor,
  });

  return evaluatedFilters;
};

export const hexToRGB = (hex: string) => {
  hex = hex.replace(/^#/, '');

  let r: number,
    g: number,
    b: number,
    a: number = 1;

  // Parse based on hex string length
  if (hex.length === 3) {
    // #RGB format
    r = parseInt(hex.charAt(0) + hex.charAt(0), 16);
    g = parseInt(hex.charAt(1) + hex.charAt(1), 16);
    b = parseInt(hex.charAt(2) + hex.charAt(2), 16);
  } else if (hex.length === 4) {
    // #RGBA format
    r = parseInt(hex.charAt(0) + hex.charAt(0), 16);
    g = parseInt(hex.charAt(1) + hex.charAt(1), 16);
    b = parseInt(hex.charAt(2) + hex.charAt(2), 16);
    a = parseInt(hex.charAt(3) + hex.charAt(3), 16) / 255;
  } else if (hex.length === 6) {
    // #RRGGBB format
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  } else if (hex.length === 8) {
    // #RRGGBBAA format
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
    a = parseInt(hex.substring(6, 8), 16) / 255;
  } else {
    // Invalid hex, return a default rgba
    throw new Error(`Invalid hex color format: ${hex}`);
  }

  // Round alpha to 2 decimal places for readability
  a = Math.round(a * 100) / 100;

  // Ensure RGB values are valid numbers
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    throw new Error(`Invalid hex color values in: ${hex}`);
  }

  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

export const RICH_TEXT_EDITOR_CONFIG = {
  readonly: false,
  placeholder: 'Start typing...',
  height: 300,
  buttons: [
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'ul',
      'ol',
      '|',
      'outdent',
      'indent',
      '|',
      'font',
      'fontsize',
      'brush',
      'paragraph',
      '|',
      'image',
      'link',
      'table',
      '|',
      'align',
      'undo',
      'redo',
      '|',
      'hr',
      'eraser',
      'fullsize',
      'print',
  ],
  uploader: {
      insertImageAsBase64URI: true,
  },
  removeButtons: ['source'],
  showXPathInStatusbar: false,
};

